import React, { Component } from 'react';
import { Helmet } from 'react-helmet';
import {
  Spinner,
  NonIdealState,
  Icon,
  Popover,
  Menu, MenuItem, Intent
} from '@blueprintjs/core';
import { observer, inject } from 'mobx-react';
import moment from 'moment-timezone';
import JobMapView from 'components/Job/MapView';
import JobHeader from 'components/Job/Header';
import WorkOrdersTable from 'components/Job/WorkOrdersTable';
import EstimatesTable from 'components/Job/EstimatesTable';
import InvoicesTable from 'components/Job/InvoicesTable';
import TimeLogsTable from 'components/Job/TimeLogsTable';
import JobsActivityLogs from 'components/Job/JobsActivityLogs';
import NotesTable from 'components/Job/NotesTable';
import AddWorkReportModal from 'components/WorkReport/AddWorkReportModal';
import AddEstimateModal from 'components/Quote/AddEstimateModal';
import AddInvoiceModal from 'components/Invoice/AddInvoiceModal';
import { JobCardHolder } from 'components/Styles';
import { formatTime } from '../../helpers/time';
import { jobTabPath } from '../../helpers/jobTabPath';
import { observable } from 'mobx';
import ImagesTable from 'components/Job/ImagesTable';
import Toast from 'helpers/toaster';

const STATUS_OPTIONS = [
  { value: 'assigned', label: 'Assigned' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'completed', label: 'Completed' },
];

const initialsOf = (name = '') =>
  String(name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join('');

const lengthLabel = (start, end) => {
  if (!start || !end) return null;
  const minutes = moment(end).diff(moment(start), 'minutes');
  if (!(minutes > 0)) return null;
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const mins = minutes % 60;
  return [days && `${days} ${days === 1 ? 'day' : 'days'}`, hours && `${hours} ${hours === 1 ? 'hr' : 'hrs'}`, mins && `${mins} min`]
    .filter(Boolean).join(' ');
};

// When a job happens, in words: "Wed, Oct 14, 2026" and "12:55 – 1:25 PM"
// (or the end day too when it runs past midnight).
const whenOf = (start, end) => {
  if (!start) return { day: 'Not scheduled', time: null };
  const s = moment(start);
  const e = end ? moment(end) : null;
  const day = s.format('ddd, MMM D, YYYY');
  if (!e) return { day, time: s.format('h:mm A') };
  const time = s.isSame(e, 'day')
    ? `${s.format('h:mm')} – ${e.format('h:mm A')}`
    : `${s.format('h:mm A')} – ${e.format('ddd, MMM D, h:mm A')}`;
  return { day, time };
};

// Where the job sits in time, as a coloured pill: today, coming up, past, or
// overdue (ended, but not everyone going has finished).
const timingOf = (start, end, assignments) => {
  if (!start) return null;
  const now = moment();
  const s = moment(start);
  const e = end ? moment(end) : s;
  const active = (assignments || []).filter((assignment) => assignment.status !== 'unassigned');
  const finished = active.length > 0 && active.every((assignment) => assignment.status === 'completed');
  if (e.isBefore(now) && !finished && active.length > 0) return { tone: 'overdue', label: 'Overdue' };
  if (s.isSame(now, 'day') || (s.isBefore(now) && e.isAfter(now))) return { tone: 'today', label: 'Today' };
  if (s.isSame(moment(now).add(1, 'day'), 'day')) return { tone: 'soon', label: 'Tomorrow' };
  if (s.isAfter(now)) return { tone: 'soon', label: `In ${s.diff(now.clone().startOf('day'), 'days')} days` };
  const ago = now.clone().startOf('day').diff(e.clone().startOf('day'), 'days');
  return { tone: 'past', label: ago === 1 ? 'Yesterday' : `${ago} days ago` };
};

// The job's facts, beside the tabs: when, who's going (and how far along
// they are), the customer and where to go.
@inject('store', 'viewStore', 'jobStore') @observer
class JobDetails extends React.Component {

  handleChangeAssignmentStatus(selectedOption, assignment) {
    let {
      jobStore
    } = this.props;
    let event = "";
    switch(selectedOption) {
      case 'in_progress':
        event = "start";
        break;
      case "assigned":
        event = "assign";
        break;
      case "confirmed":
        event = "confirm";
        break;
      case "completed":
        event = "complete";
        break;
      default:
        event = selectedOption;
    }
    jobStore.changeAssignmentStatus(event, assignment)
      .then(() => {
        jobStore.refreshJob({uuid: assignment.job_id})
          .then((result) => {
            if (!result.applied) return;
            Toast.show({ message: `${assignment.name}: ${STATUS_OPTIONS.find((o) => o.value === selectedOption).label.toLowerCase()}.`, intent: Intent.SUCCESS })
          })
      })
      .catch((error) => {
        const errors = error.response && error.response.data && error.response.data.errors;
        Toast.show({ message: errors ? [].concat(errors).join(' ') : "Couldn’t change the status. Try again.", intent: Intent.DANGER })
      })
  }

  render() {
    const { job, jobStore: { showChangeAssignmrntStatusLoader } } = this.props;
    const a = job.attributes;
    const client = a.client || {};
    const when = whenOf(a.start_date, a.end_date);
    const length = lengthLabel(a.start_date, a.end_date);
    const timing = timingOf(a.start_date, a.end_date, a.assignments);
    const assignments = (a.assignments || []).filter((assignment) => assignment.status !== 'unassigned');
    const address = a.location_address;
    const location = a.location || {};
    const directions = location.latitude && location.longitude
      ? `https://www.google.com/maps/dir/?api=1&destination=${location.latitude},${location.longitude}`
      : address ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}` : null;

    return (
      <section className="jp-card jp-details" aria-label="Job details">
        <div className="jp-details__group">
          <h2 className="jp-details__title">Reason for call</h2>
          <p className="jp-reason__text">{a.reason_for_call || 'No reason given. Edit the job to add one.'}</p>
        </div>

        <div className="jp-details__group">
          <h2 className="jp-details__title">When</h2>
          <div className="jp-when">
            <span className="jp-when__day">
              {when.day}
              {timing && <span className={`jp-timing jp-timing--${timing.tone}`}>{timing.label}</span>}
            </span>
            {when.time && (
              <span className="jp-when__time">
                {when.time}
                {length && <span className="jp-when__length">{length}</span>}
              </span>
            )}
          </div>
          <p className="jp-details__note">Created {formatTime(a.created_at)}</p>
        </div>

        <div className="jp-details__group">
          <h2 className="jp-details__title">
            Technicians
            {showChangeAssignmrntStatusLoader && <Spinner size={14} />}
          </h2>
          {assignments.length === 0 && <p className="jp-details__empty">No technician yet. Edit the job to assign someone.</p>}
          <ul className="jp-techs">
            {assignments.map((assignment) => (
              <li key={assignment.id} className="jp-tech">
                <span className="jp-tech__avatar" aria-hidden="true">{initialsOf(assignment.name)}</span>
                <span className="jp-tech__name">{assignment.name}</span>
                <Popover minimal={true} position="bottom-right">
                  <button type="button" className={`jp-status jp-status--${assignment.status}`} aria-label={`${assignment.name}: ${assignment.formatted_status}. Change status`}>
                    {assignment.formatted_status}
                    <Icon icon="chevron-down" iconSize={12} />
                  </button>
                  <Menu>
                    {STATUS_OPTIONS.map((option) => (
                      <MenuItem
                        key={option.value}
                        text={option.label}
                        icon={assignment.status === option.value ? 'tick' : 'blank'}
                        onClick={() => this.handleChangeAssignmentStatus(option.value, assignment)}
                      />
                    ))}
                  </Menu>
                </Popover>
              </li>
            ))}
          </ul>
        </div>

        <div className="jp-details__group">
          <h2 className="jp-details__title">Customer</h2>
          <a className="jp-details__strong" href={`/dashboard/clients/${a.client_id}?resource=jobs`}>{a.client_name || client.name}</a>
          <ul className="jp-contact">
            {client.phone && <li><Icon icon="phone" iconSize={12} /><a href={`tel:${client.phone}`}>{client.phone}</a></li>}
            {client.email && <li><Icon icon="envelope" iconSize={12} /><a href={`mailto:${client.email}`}>{client.email}</a></li>}
          </ul>
          {client.tags && client.tags.length > 0 && (
            <div className="jp-tags">
              {client.tags.map((tag) => <span key={tag.id} className="jp-tag">{tag.name}</span>)}
            </div>
          )}
        </div>

        <div className="jp-details__group">
          <h2 className="jp-details__title">Location</h2>
          <p className="jp-details__text">{address || 'No address on this job.'}</p>
          {directions && (
            <a className="jp-details__link" href={directions} target="_blank" rel="noopener noreferrer">
              <Icon icon="path-search" iconSize={12} /> Get directions
            </a>
          )}
        </div>

        {(a.po_number || a.project) && (
          <div className="jp-details__group">
            <h2 className="jp-details__title">Billing</h2>
            <dl className="jp-facts">
              {a.po_number && (<><dt>PO number</dt><dd>{a.po_number}</dd></>)}
              {a.project && (<><dt>Project</dt><dd>{a.project.name}</dd></>)}
            </dl>
          </div>
        )}
      </section>
    )
  }
}

// The Overview tab: where the job is.
const JobOverview = ({ job }) => {
  const a = job.attributes;
  return (
    <section className="jp-card jp-map" aria-label="Map">
      {a.location && a.location.latitude && a.location.longitude
        ? <JobMapView job={job} height="100%" />
        : <p className="jp-details__empty jp-map__empty">This job’s address isn’t on the map yet.</p>}
    </section>
  );
};

const JOB_TABS = [
  { id: 'location', label: 'Overview' },
  { id: 'work-orders', label: 'Work orders', count: 'work_order' },
  { id: 'estimates', label: 'Estimates', count: 'estimate' },
  { id: 'invoices', label: 'Invoices', count: 'invoice' },
  { id: 'time-logs', label: 'Time logs', count: 'time_log' },
  { id: 'images', label: 'Files', count: 'images' },
  { id: 'activities', label: 'Activity', count: 'activities' },
];

@inject('store', 'jobStore','viewStore', 'activityStore', 'workReportStore', 'quoteStore', 'invoiceStore') @observer
class JobShowPage extends Component {

  get canRender() {
    let {
      jobStore: {
        job: {
          isLoading,
          isError,
          object
        }
      }
    } = this.props;
    return (!isLoading && !isError && object);
  }

  @observable selectedTabId = 'location';
  @observable.ref headerActions = null; // Add time log / Upload files, from JobHeader
  @observable isOpenAddWorkReportDialog = false;
  @observable isOpenAddEstimateDialog = false;
  @observable isOpenAddInvoiceDialog = false;

  pageRef = React.createRef();

  componentDidMount() {
    this.selectedTabId = this.props.viewStore.jobTab.section;
    this.handleTabChange(this.selectedTabId)
    window.addEventListener('resize', this.fitToWindow);
    this.fitToWindow();
  }

  componentDidUpdate() {
    this.fitToWindow();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.fitToWindow);
    cancelAnimationFrame(this.fitFrame);
  }

  // The page fills exactly the window below the top bar, so the window never
  // scrolls; each column scrolls on its own only when it has more than fits.
  // Measured rather than a fixed offset: the bars above (and any spacing
  // below) differ with window size and zoom.
  fitToWindow = () => {
    cancelAnimationFrame(this.fitFrame);
    this.fitFrame = requestAnimationFrame(() => {
      const page = this.pageRef.current;
      if (!page) return;
      if (window.matchMedia && window.matchMedia('(max-width: 760px)').matches) {
        page.style.height = '';
        return;
      }
      const top = page.getBoundingClientRect().top + window.scrollY;
      let height = Math.max(420, Math.floor(window.innerHeight - top));
      page.style.height = `${height}px`;
      const overflow = document.documentElement.scrollHeight - window.innerHeight;
      if (overflow > 0) {
        height = Math.max(420, height - overflow);
        page.style.height = `${height}px`;
      }
    });
  };
  
  handleTabChange = (tabId) => {
    let {
      viewStore,
      jobStore: {
        job: { object }
      }

    } = this.props;
    this.selectedTabId = tabId; 
    viewStore.jobTab.section = tabId;
    // Only the job this screen is showing. On mount the store can still hold
    // the previous job, and writing that id makes refresh load the wrong job.
    const path = jobTabPath(object, tabId, viewStore.currentPath);
    if (path) {
      window.history.pushState({}, '', path);
    }
  }

  handleWorkReportClick() {
    let job = this.props.jobStore.job.object;
    let { object } = this.props.workReportStore.newWorkReport;

    // client pre selected for work order
    let client_details = {
      attributes: {
        name: ''
      }
    };
    client_details.attributes = job.attributes.client;
    object.selectedClient = client_details;
    object.client_id = job.attributes.client.id;

    // job pre selected for work order
    let job_details = [];
    job_details = [{
      id: job.attributes.id,
      label: job.attributes.title,
      value: job.attributes.id,
      selected: true
    }]
    object.selectedJob = job_details;
    object.scheduled_event_id = job.attributes.id;

    // location pre selected for work order
    let location_details = {
      attributes: job.attributes.location
    };
    object.location = location_details;
    object.location_id = job.attributes.location.id;

    // job search using client id for work order
    this.props.viewStore.jobSearch.where.client_id = job.attributes.client.id;
    this.isOpenAddWorkReportDialog = true;
  }

  handleCloseWorkReportDailog() {
    this.isOpenAddWorkReportDialog = false;
    let { object } = this.props.workReportStore.newWorkReport;
    object.selectedJob = null;
    object.selectedClient = null;
    object.client_id = '';
    object.scheduled_event_id = '';
  }

  onSuccessAddWorkReport(workReport) {
    this.isOpenAddWorkReportDialog = false;
  }

  handleEstimateClick() {
    let job = this.props.jobStore.job.object;
    let { object } = this.props.quoteStore.newEstimate;

    // client pre selected for work order
    let client_details = {
      attributes: {
        name: ''
      }
    };
    client_details.attributes = job.attributes.client;
    object.selectedClient = client_details;
    object.client_id = job.attributes.client.id;

    // job pre selected for work order
    let job_details = [];
    job_details = [{
      id: job.attributes.id,
      label: job.attributes.title,
      value: job.attributes.id,
      selected: true
    }]
    object.selectedJob = job_details;
    object.scheduled_event_id = job.attributes.id;

    // location pre selected for work order
    let location_details = {
      attributes: job.attributes.location
    };
    object.location = location_details;
    object.location_id = job.attributes.location.id;

    // job search using client id for work order
    this.props.viewStore.jobSearch.where.client_id = job.attributes.client.id;
    this.isOpenAddEstimateDialog = true;
  }

  handleCloseEstimateDailog() {
    this.isOpenAddEstimateDialog = false;
    let { object } = this.props.quoteStore.newEstimate;
    object.selectedJob = null;
    object.selectedClient = null;
    object.client_id = '';
    object.scheduled_event_id = '';
  }

  onSuccessAddEstimate(estimate) {
    this.isOpenAddEstimateDialog = false;
  }

  handleInvoiceClick() {
    let job = this.props.jobStore.job.object;
    let { object } = this.props.invoiceStore.newInvoice;

    // client pre selected for work order
    let client_details = {
      attributes: {
        name: ''
      }
    };
    client_details.attributes = job.attributes.client;
    object.selectedClient = client_details;
    object.client_id = job.attributes.client.id;

    // job pre selected for work order
    let job_details = [];
    job_details = [{
      id: job.attributes.id,
      label: job.attributes.title,
      value: job.attributes.id,
      selected: true
    }]
    object.selectedJob = job_details;
    object.scheduled_event_id = job.attributes.id;

    // location pre selected for work order
    let location_details = {
      attributes: job.attributes.location
    };
    object.location = location_details;
    object.location_id = job.attributes.location.id;

    // job search using client id for work order
    this.props.viewStore.jobSearch.where.client_id = job.attributes.client.id;
    this.isOpenAddInvoiceDialog = true;
  }

  handleCloseInvoiceDailog() {
    this.isOpenAddInvoiceDialog = false;
    let { object } = this.props.invoiceStore.newInvoice;
    object.selectedJob = null;
    object.selectedClient = null;
    object.client_id = '';
    object.scheduled_event_id = '';
  }

  onSuccessAddInvoice(invoice) {
    this.isOpenAddInvoiceDialog = false;
  }

  // The selected tab's bar: how many on the left, a permanent add button on
  // the right. Overview and Activity have nothing to add.
  renderPanelHead(object) {
    const counts = object.attributes.counts || {};
    const header = this.headerActions || {};
    const hasWorkOrders = counts.work_order > 0;
    const PANEL = {
      'work-orders': { label: 'Add work order', icon: 'add', onClick: this.handleWorkReportClick.bind(this), count: counts.work_order, noun: ['work order', 'work orders'] },
      estimates: { label: 'Add estimate', icon: 'add', onClick: this.handleEstimateClick.bind(this), count: counts.estimate, noun: ['estimate', 'estimates'] },
      invoices: { label: 'Add invoice', icon: 'add', onClick: this.handleInvoiceClick.bind(this), count: counts.invoice, noun: ['invoice', 'invoices'] },
      'time-logs': {
        label: 'Add time log',
        icon: 'add',
        onClick: header.addTimeLog,
        disabled: !hasWorkOrders,
        hint: hasWorkOrders ? null : 'Add a work order first',
        count: counts.time_log,
        noun: ['time log', 'time logs'],
      },
      images: { label: 'Upload files', icon: 'paperclip', onClick: header.uploadFiles, count: counts.images, noun: ['file', 'files'] },
    };
    const panel = PANEL[this.selectedTabId];
    if (!panel) return null;
    const count = panel.count || 0;
    // Time logs: the hours, added up.
    let total = null;
    if (this.selectedTabId === 'time-logs') {
      const logs = (this.props.jobStore.timeLogs && this.props.jobStore.timeLogs.objects) || [];
      const minutes = logs.reduce((sum, log) => {
        const a = log.attributes;
        return sum + (a.start_time && a.end_time ? Math.max(0, moment(a.end_time).diff(moment(a.start_time), 'minutes')) : 0);
      }, 0);
      if (minutes > 0) {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        total = [hours && `${hours} ${hours === 1 ? 'hr' : 'hrs'}`, mins && `${mins} min`].filter(Boolean).join(' ');
      }
    }
    return (
      <div className="jp-panel-head">
        <span className="jp-panel-head__count">
          {count} {count === 1 ? panel.noun[0] : panel.noun[1]}
          {total && <span className="jp-panel-head__total">{total}</span>}
        </span>
        {panel.hint && <span className="jp-panel-head__hint">{panel.hint}</span>}
        <button
          type="button"
          className="jp-add"
          onClick={panel.onClick}
          disabled={panel.disabled || !panel.onClick}
          title={panel.hint || undefined}
        >
          <Icon icon={panel.icon} iconSize={14} />
          {panel.label}
        </button>
      </div>
    );
  }

  render() {
    let content, title;
    let {selectedTabId} = this;
    let {
      jobStore: {
        job: {
          isLoading,
          isError,
          object
        }
      }
    } = this.props;
    if (isLoading && !isError) {
      title = 'Loading..';
      content = <JobCardHolder><Spinner/></JobCardHolder>;
    }
    if (isError && !isLoading) {
      title = 'Failed to Load Job';
      content = (
        <NonIdealState
          icon="search"
          title="This job couldn’t be opened"
          description="It may have been deleted, belong to another account, or you may be offline. Find it in the Jobs list instead."
        />
      );
    }
    if (this.canRender && object.attributes) {
      title = `Job #${object.attributes.event_number} | Field Nexus`;
    }else{
      title = `Loading...`;
    }
    return (
      <React.Fragment>
        {
          <Helmet>
            <title>{title}</title>
          </Helmet>
        }
        {
          // Loading, or the job couldn't be loaded (deleted, another account,
          // offline): say so instead of a blank page.
          !this.canRender && content 
        }
        {
          this.canRender && object.attributes && (
            <div className="cp jp" ref={this.pageRef}>
              <JobHeader
                job={object}
                registerActions={(actions) => { this.headerActions = actions; }}
                handleWorkReportClick={this.handleWorkReportClick.bind(this)}
                handleEstimateClick={this.handleEstimateClick.bind(this)}
                handleInvoiceClick={this.handleInvoiceClick.bind(this)}
              />
              <div className="jp-body">
                <section className="cp-main jp-main" aria-label="Job records">
                  <div className="cp-tabs jp-tabs" role="tablist" aria-label="Job records">
                    {JOB_TABS.map((tab) => {
                      const active = selectedTabId === tab.id;
                      const count = tab.count ? (object.attributes.counts || {})[tab.count] : null;
                      return (
                        <button
                          key={tab.id}
                          type="button"
                          role="tab"
                          aria-selected={active}
                          className={`cp-tab${active ? ' is-active' : ''}`}
                          onClick={() => this.handleTabChange(tab.id)}
                        >
                          <span className="cp-tab__label">{tab.label}</span>
                          {tab.count
                            ? <span className="cp-tab__count">{count || 0}</span>
                            : <span className="cp-tab__count jp-tab__icon"><Icon icon="map-marker" iconSize={20} /></span>}
                        </button>
                      );
                    })}
                  </div>
                  <div className={`jp-tab-panel jp-tab-panel--${selectedTabId}`} role="tabpanel">
                    {this.renderPanelHead(object)}
                    {selectedTabId === 'location' && <JobOverview job={object} />}
                    {selectedTabId === 'work-orders' &&
                      <WorkOrdersTable job={object} handleWorkReportClick={this.handleWorkReportClick.bind(this)}/>}
                    {selectedTabId === 'estimates' &&
                      <EstimatesTable job={object} handleEstimateClick={this.handleEstimateClick.bind(this)}/>}
                    {selectedTabId === 'invoices' &&
                      <InvoicesTable job={object} handleInvoiceClick={this.handleInvoiceClick.bind(this)}/>}
                    {selectedTabId === 'time-logs' && <TimeLogsTable job={object} />}
                    {selectedTabId === 'images' && <ImagesTable job={object} />}
                    {selectedTabId === 'activities' && <JobsActivityLogs job={object} />}
                  </div>
                </section>
                <aside className="jp-side" aria-label="Job details">
                  <JobDetails job={object} />
                </aside>
                <aside className="jp-side jp-side--notes" aria-label="Notes">
                  <section className="jp-card jp-notes" aria-label="Notes">
                    <NotesTable job={object} />
                  </section>
                </aside>
              </div>
            </div>
          )
        }
        {/* <div style={{ backgroundColor: 'white', borderLeft: '1px solid #E8ECF0', width: '400px', marginTop: '2px', height: '100vh', boxShadow: '0 0 0 1px rgba(16, 22, 26, 0.1), 0 0 0 rgba(16, 22, 26, 0), 0 1px 1px rgba(16, 22, 26, 0.2)' }}>
          <p>hello boss {content}</p>
        </div> */}
        <AddWorkReportModal
          isOpen={this.isOpenAddWorkReportDialog}
          onClose={this.handleCloseWorkReportDailog.bind(this)}
          onSuccess={this.onSuccessAddWorkReport.bind(this)}
          edit={true}
          job={this.props.jobStore.job.object}
        />
        <AddEstimateModal
          isOpen={this.isOpenAddEstimateDialog}
          onClose={this.handleCloseEstimateDailog.bind(this)}
          onSuccess={this.onSuccessAddEstimate.bind(this)}
          edit={true}
          job={this.props.jobStore.job.object}
        />
        <AddInvoiceModal
          isOpen={this.isOpenAddInvoiceDialog}
          onClose={this.handleCloseInvoiceDailog.bind(this)}
          onSuccess={this.onSuccessAddInvoice.bind(this)}
          edit={true}
          job={this.props.jobStore.job.object}
        />
      </React.Fragment>
    )
  }

};

export default JobShowPage;